import { NumberField as BaseNumberField } from '@base-ui/react/number-field';
import React, { ComponentProps, useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box } from '@mui/material';
import OutlinedInput from '@mui/material/OutlinedInput';

import {
  FormHelperText,
  IconButton,
  InputAdornment,
  withTitle,
} from '@sbv-clients/ui-shared';
import { Density } from '@sbv-clients/util-misc';

import { useFormFieldError } from '../../hooks/use-field-error';

type NumberFieldAdditionalProps = {
  name: string;
  showHelperText?: boolean;
  endAdornment?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  infoText?: React.ReactNode;
  min?: number;
  max?: number;
};

export type FormNumberFieldProps = NumberFieldAdditionalProps & {
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  sx?: Record<string, any>;
  inputSx?: Record<string, any>;
  helperText?: React.ReactNode;
  placeholder?: string;
  density?: Density;
};

const BaseFormNumberField = ({
  name,
  size = 'medium',
  fullWidth,
  disabled,
  inputSx,
  showHelperText = true,
  onClick,
  endAdornment,
  infoText,
  placeholder,
  min,
  max,
  onChange,
  onBlur,
  density = 'compact',
}: FormNumberFieldProps) => {
  const { helperText } = useFormFieldError(name);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => {
      onClick?.(event);
    },
    [onClick]
  );
  const height = density === 'comfortable' ? 42 : size === 'small' ? 32 : 48;
  const defaultEndAdornment = useMemo(() => {
    return (
      <InputAdornment
        position="end"
        sx={{
          flexDirection: 'column',
          maxHeight: '100%',
          height: '100%',
          alignSelf: 'stretch',
          borderLeft: '1px solid',
          borderColor: 'divider',
          ml: 0,
          '& button': {
            py: 0,
            flex: 1,
            borderRadius: 0.5,
          },
        }}
      >
        <BaseNumberField.Increment
          render={<IconButton size={size} aria-label="Increase" />}
        >
          <KeyboardArrowUpIcon
            fontSize={size}
            sx={{ transform: 'translateY(2px)', height: `${height / 2}px` }}
          />
        </BaseNumberField.Increment>

        <BaseNumberField.Decrement
          render={<IconButton size={size} aria-label="Decrease" />}
        >
          <KeyboardArrowDownIcon
            fontSize={size}
            sx={{ transform: 'translateY(-2px)', height: `${height / 2}px` }}
          />
        </BaseNumberField.Decrement>
      </InputAdornment>
    );
  }, [height, size]);

  const render: ComponentProps<typeof Controller>['render'] = useCallback(
    ({ field }) => {
      const value = field.value ?? '';
      return (
        <Box>
          <BaseNumberField.Root
            value={value}
            onValueChange={(val) => {
              field.onChange(val);
            }}
            min={min}
            max={max}
            disabled={disabled}
            render={() => (
              <BaseNumberField.Input
                render={(inputProps) => {
                  const {
                    color,
                    ref,
                    onChange: baseOnChange,
                    ...rest
                  } = inputProps;

                  return (
                    <OutlinedInput
                      inputRef={ref}
                      {...rest}
                      placeholder={placeholder}
                      onChange={(e) => {
                        baseOnChange?.(
                          e as React.ChangeEvent<HTMLInputElement>
                        );
                        onChange?.(e as React.ChangeEvent<HTMLInputElement>);
                      }}
                      onBlur={onBlur}
                      disabled={disabled}
                      sx={{
                        pr: 0,
                        overflow: 'hidden',
                        height,
                        '& .MuiOutlinedInput-input': {
                          minHeight: height,
                          padding: size === 'small' ? '10px 12px' : '14px 12px',
                          boxSizing: 'border-box',
                        },
                        ...inputSx,
                      }}
                      endAdornment={endAdornment ?? defaultEndAdornment}
                      onClick={handleClick}
                      fullWidth={fullWidth}
                    />
                  );
                }}
              />
            )}
          />
          {Boolean(infoText) && <FormHelperText>{infoText}</FormHelperText>}
          {showHelperText && Boolean(helperText) && (
            <FormHelperText error>{helperText}</FormHelperText>
          )}
        </Box>
      );
    },
    [
      min,
      max,
      disabled,
      infoText,
      showHelperText,
      helperText,
      placeholder,
      onBlur,
      height,
      size,
      inputSx,
      endAdornment,
      defaultEndAdornment,
      handleClick,
      fullWidth,
      onChange,
    ]
  );

  return <Controller name={name} render={render} />;
};

export const FormNumberField = withTitle(BaseFormNumberField);
